import type {
  ActiveDelivery,
  DeliveryScore,
  GeocodeResult,
  SessionMode,
} from '@roadworld/shared';
import { foodIcon } from '../delivery/foodIcons.js';
import type { VehicleKind } from '../vehicle/IVehicle.js';
import type { RoadDetail } from '../world/surface.js';
import { loadAll, remove, save, type SavedLocation } from './savedLocations.js';

export interface HudHandlers {
  onGenerate(locationName: string): void;
  /** Called when the user picks a saved location. Receives the resolved geocode result
   *  directly, so main.ts can skip the geocoding roundtrip. */
  onLoadSaved(loc: SavedLocation): void;
  /** Called when the user clicks the "Switch to X" button. main.ts swaps the vehicle and
   *  then calls `setActiveVehicle()` back on the HUD to refresh the label. */
  onVehicleSwap(): void;
  onToggleClock(): void;
  onSetMode(mode: SessionMode): void;
  onToggleLod(enabled: boolean): void;
}

export interface PerfStats {
  gpuMs: number | null;
  drawCalls: number;
  activeTris: number;
}

export class Hud {
  private fpsEl: HTMLElement;
  private gpuMsEl: HTMLElement;
  private drawCallsEl: HTMLElement;
  private activeTrisEl: HTMLElement;
  private lodToggle: HTMLInputElement;
  private rpmEl: HTMLElement;
  private speedEl: HTMLElement;
  private coordsEl: HTMLElement;
  private statusEl: HTMLElement;
  private input: HTMLInputElement;
  private button: HTMLButtonElement;
  private saveButton: HTMLButtonElement;
  private savedToggle: HTMLButtonElement;
  private savedToggleLabel: HTMLElement;
  private savedPanel: HTMLElement;
  private minimapCanvas: HTMLCanvasElement;
  private minimapModeLabel: HTMLElement;
  private minimapZoomIn: HTMLButtonElement;
  private minimapZoomOut: HTMLButtonElement;
  private minimapZoomLabel: HTMLElement;
  private settingsButton: HTMLButtonElement;
  private minimapAreaLabel: HTMLElement;
  private vehicleToggle: HTMLButtonElement;
  private vehicleToggleLabel: HTMLElement;
  private perfOverlayEl: HTMLElement;
  // Photo-mode containers — cached so setPhotoMode flips them without re-querying.
  private photoModeEls: HTMLElement[] = [];
  // Delivery elements
  private clockToggle: HTMLButtonElement;
  private deliveryModeLabel: HTMLElement;
  private sessionScoreEl: HTMLElement;
  private persistMoneyEl: HTMLElement;
  private hostControlsEl: HTMLElement;
  private modeButtons: HTMLButtonElement[] = [];
  private deliveryPillsEl: HTMLElement;
  /** Live `ActiveDelivery` snapshot. Kept here so the per-frame timer animation can read
   *  it without bouncing through main.ts. */
  private activeDeliveries: ActiveDelivery[] = [];
  // Road-class debug HUD (toggle: B)
  private roadDebugPanel: HTMLElement;
  private roadDebugClass: HTMLElement;
  private roadDebugMeta: HTMLElement;
  private roadDebugVisible = false;
  private lastFpsUpdate = 0;
  private lastPerfUpdate = 0;
  /** Currently loaded location — null until the first successful load. Drives the Save button's enabled state. */
  private activeLocation: GeocodeResult | null = null;
  private handlers: HudHandlers;

  constructor(handlers: HudHandlers) {
    this.handlers = handlers;
    this.fpsEl = required('fpsLabel');
    this.gpuMsEl = required('gpuMsLabel');
    this.drawCallsEl = required('drawCallsLabel');
    this.activeTrisEl = required('activeTrisLabel');
    this.lodToggle = required('lodToggle') as HTMLInputElement;
    this.rpmEl = required('rpmLabel');
    this.speedEl = required('speedLabel');
    this.coordsEl = required('coordsLabel');
    this.statusEl = required('statusLine');
    this.input = required('locationInput') as HTMLInputElement;
    this.button = required('generateButton') as HTMLButtonElement;
    this.saveButton = required('saveButton') as HTMLButtonElement;
    this.savedToggle = required('savedToggle') as HTMLButtonElement;
    this.savedToggleLabel = required('savedToggleLabel');
    this.savedPanel = required('savedPanel');
    this.minimapCanvas = required('minimapCanvas') as HTMLCanvasElement;
    this.minimapModeLabel = required('minimapModeLabel');
    this.minimapZoomIn = required('minimapZoomIn') as HTMLButtonElement;
    this.minimapZoomOut = required('minimapZoomOut') as HTMLButtonElement;
    this.minimapZoomLabel = required('minimapZoomLabel');
    this.settingsButton = required('settingsButton') as HTMLButtonElement;
    this.minimapAreaLabel = required('minimapAreaLabel');
    this.vehicleToggle = required('vehicleToggle') as HTMLButtonElement;
    this.vehicleToggleLabel = required('vehicleToggleLabel');
    this.perfOverlayEl = required('perfOverlay');
    this.clockToggle = required('clockToggle') as HTMLButtonElement;
    this.deliveryModeLabel = required('deliveryMode');
    this.sessionScoreEl = required('sessionScore');
    this.persistMoneyEl = required('persistMoney');
    this.hostControlsEl = required('hostControls');
    this.modeButtons = Array.from(
      this.hostControlsEl.querySelectorAll<HTMLButtonElement>('.mode-btn'),
    );
    this.deliveryPillsEl = required('deliveryPills');
    this.roadDebugPanel = required('roadDebugPanel');
    this.roadDebugClass = required('roadDebugClass');
    this.roadDebugMeta = required('roadDebugMeta');

    // Photo-mode targets: minimap card, top location bar, left-side delivery card, and
    // the top-center delivery pills. requiredSelector() throws if any are missing so the
    // selectors stay in sync with the DOM.
    this.photoModeEls = [
      requiredSelector('.hud-top-right'),
      requiredSelector('.hud-top'),
      requiredSelector('.hud-mid-left'),
      this.deliveryPillsEl,
    ];

    const submit = () => {
      const name = this.input.value.trim();
      if (!name) return;
      handlers.onGenerate(name);
    };
    this.button.addEventListener('click', submit);
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });

    this.vehicleToggle.addEventListener('click', () => this.handlers.onVehicleSwap());

    this.saveButton.addEventListener('click', () => this.saveCurrent());
    this.savedToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleSavedPanel();
    });
    // Click anywhere else closes the dropdown.
    document.addEventListener('click', (e) => {
      if (this.savedPanel.hidden) return;
      const target = e.target as Node | null;
      if (target && !this.savedPanel.contains(target) && target !== this.savedToggle) {
        this.closeSavedPanel();
      }
    });

    this.clockToggle.addEventListener('click', () => handlers.onToggleClock());
    for (const btn of this.modeButtons) {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode as SessionMode | undefined;
        if (mode) handlers.onSetMode(mode);
      });
    }
    this.lodToggle.addEventListener('change', () => handlers.onToggleLod(this.lodToggle.checked));
    window.addEventListener('keydown', (e) => {
      // L toggles LOD. Ignore when focus is in a text input to avoid hijacking typing.
      if (e.key !== 'l' && e.key !== 'L') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      this.lodToggle.checked = !this.lodToggle.checked;
      handlers.onToggleLod(this.lodToggle.checked);
    });

    this.refreshSavedList();
    this.updateSaveButton();
  }

  // ---------- Delivery ----------

  setOnClock(on: boolean): void {
    this.clockToggle.textContent = on ? '■ Stop working' : '▶ Start working';
    this.clockToggle.classList.toggle('on-clock', on);
  }

  setSessionMode(mode: SessionMode): void {
    const label = mode === 'freeroam' ? 'Free-roam' : mode === 'coop' ? 'Co-op' : 'Race';
    this.deliveryModeLabel.textContent = label;
    for (const btn of this.modeButtons) {
      btn.classList.toggle('active', btn.dataset.mode === mode);
    }
  }

  setHostControlsVisible(visible: boolean): void {
    this.hostControlsEl.hidden = !visible;
  }

  setScore(score: DeliveryScore): void {
    this.sessionScoreEl.textContent = score.sessionScore.toString();
    this.persistMoneyEl.textContent = Math.round(score.persistentMoney).toString();
  }

  /** Replace the visible delivery pill row. Called whenever the server sends the
   *  player's active-deliveries list (pickup / drop / mode change / hello). */
  setActiveDeliveries(active: ActiveDelivery[]): void {
    this.activeDeliveries = active;
    const pills: HTMLElement[] = [];
    for (const ad of active) {
      pills.push(this.buildPill(ad));
    }
    this.deliveryPillsEl.replaceChildren(...pills);
    this.tickPillTimers(performance.now());
  }

  /** Per-frame: drive the countdown bar widths off server time. Called from main.ts.
   *  `serverNow` is the multiplayer client's serverNow() so the bar matches the bonus
   *  window even with clock skew. */
  tickDeliveryTimers(serverNow: number): void {
    this.tickPillTimers(serverNow);
  }

  private buildPill(ad: ActiveDelivery): HTMLElement {
    const root = document.createElement('div');
    root.className = 'delivery-pill';
    if (ad.premium) root.classList.add('premium');
    root.dataset.adId = ad.id;
    root.dataset.bonusUntil = String(ad.bonusUntil);
    root.dataset.windowMs = String(ad.windowMs);

    const icon = document.createElement('div');
    icon.className = 'pill-icon';
    icon.textContent = foodIcon(ad.food);

    const text = document.createElement('div');
    text.className = 'pill-text';

    const name = document.createElement('div');
    name.className = 'pill-name';
    name.textContent = `→ ${ad.dropoffName}`;

    const meta = document.createElement('div');
    meta.className = 'pill-meta';
    const payLabel = document.createElement('span');
    payLabel.textContent = `from ${ad.pickupName} · base $${ad.basePay}`;
    meta.appendChild(payLabel);

    text.appendChild(name);
    text.appendChild(meta);

    const bar = document.createElement('div');
    bar.className = 'pill-bar';
    bar.style.width = '100%';

    root.appendChild(icon);
    root.appendChild(text);
    root.appendChild(bar);
    return root;
  }

  private tickPillTimers(serverNow: number): void {
    const children = this.deliveryPillsEl.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i] as HTMLElement;
      const bonusUntil = Number(el.dataset.bonusUntil ?? 0);
      const windowMs = Number(el.dataset.windowMs ?? 1);
      const remaining = Math.max(0, bonusUntil - serverNow);
      const frac = Math.max(0, Math.min(1, remaining / windowMs));
      const bar = el.querySelector<HTMLElement>('.pill-bar');
      if (bar) bar.style.width = `${(frac * 100).toFixed(1)}%`;
      el.classList.toggle('cold', frac === 0);
    }
    // Suppress unused warning in case the snapshot is ever read elsewhere.
    void this.activeDeliveries;
  }

  /** Show / hide the bottom-left perf overlay (FPS / GPU / Draws / Tris / LOD). Driven
   *  by the Debug section's "Show debug overlay" toggle. The Coords row below it stays
   *  always-visible regardless. */
  setDebugOverlayVisible(visible: boolean): void {
    this.perfOverlayEl.hidden = !visible;
  }

  /** Photo mode: hide the minimap, location bar, delivery card, and delivery pills so
   *  the player can take a clean screenshot. Driven by the visualSection toggle and the
   *  `P` hotkey — both call this method, so the two stay in sync. */
  setPhotoMode(enabled: boolean): void {
    for (const el of this.photoModeEls) el.hidden = enabled;
  }

  setLocationField(value: string): void {
    this.input.value = value;
  }

  /** Tell the HUD which location is currently in-world. Enables Save and updates coords. */
  setActiveLocation(loc: GeocodeResult): void {
    this.activeLocation = loc;
    this.setCoords(loc.lat, loc.lng, displayShort(loc.displayName));
    this.updateSaveButton();
  }

  setCoords(lat: number, lng: number, name?: string): void {
    const text = name ? `${name} (${lat.toFixed(4)}, ${lng.toFixed(4)})` : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    this.coordsEl.textContent = text;
  }

  setRpm(rpm: number): void {
    this.rpmEl.textContent = Math.round(rpm).toString();
  }

  setSpeed(kmh: number): void {
    this.speedEl.textContent = Math.round(kmh).toString();
  }

  setFps(fps: number, now: number): void {
    if (now - this.lastFpsUpdate < 200) return;
    this.lastFpsUpdate = now;
    this.fpsEl.textContent = fps.toFixed(0);
  }

  setPerfStats(stats: PerfStats, now: number): void {
    if (now - this.lastPerfUpdate < 200) return;
    this.lastPerfUpdate = now;
    this.gpuMsEl.textContent = stats.gpuMs === null ? 'n/a' : stats.gpuMs.toFixed(1);
    this.drawCallsEl.textContent = stats.drawCalls.toString();
    this.activeTrisEl.textContent = formatCount(stats.activeTris);
  }

  setLodEnabled(enabled: boolean): void {
    this.lodToggle.checked = enabled;
  }

  setStatus(message: string, kind: 'info' | 'error' = 'info'): void {
    this.statusEl.textContent = message;
    this.statusEl.classList.toggle('error', kind === 'error');
  }

  setBusy(busy: boolean): void {
    this.button.disabled = busy;
    this.input.disabled = busy;
    this.saveButton.disabled = busy || this.activeLocation === null;
    this.savedToggle.disabled = busy;
    // Vehicle swap also no-ops during load (main.ts guards on `busy || !chunks.isReady()`);
    // mirror that on the button so the click gives visual feedback instead of silently
    // doing nothing.
    this.vehicleToggle.disabled = busy;
  }

  getMinimapCanvas(): HTMLCanvasElement {
    return this.minimapCanvas;
  }

  setMinimapMode(mode: 'north' | 'heading'): void {
    this.minimapModeLabel.textContent = mode === 'north' ? 'North · N' : 'Heading · N';
  }

  // --------- Road-class debug HUD (toggle: B) ---------

  /** Toggle the debug panel's visibility. Returns the new state. */
  toggleRoadDebug(): boolean {
    this.roadDebugVisible = !this.roadDebugVisible;
    this.roadDebugPanel.hidden = !this.roadDebugVisible;
    return this.roadDebugVisible;
  }

  isRoadDebugVisible(): boolean {
    return this.roadDebugVisible;
  }

  /**
   * Update the debug readout with the road metadata under the car. Pass null when the car
   * is off-road; the panel then shows "(off-road)". No-op while the panel is hidden.
   */
  setRoadDebug(detail: RoadDetail | null): void {
    if (!this.roadDebugVisible) return;
    if (!detail) {
      this.roadDebugClass.textContent = '(off-road)';
      this.roadDebugMeta.textContent = '';
      return;
    }
    this.roadDebugClass.textContent = detail.roadClass;
    const parts: string[] = [];
    parts.push(`width ${(detail.halfWidth * 2).toFixed(1)}m`);
    if (detail.lanes !== undefined) parts.push(`lanes ${detail.lanes}`);
    if (detail.oneway) parts.push('oneway');
    if (detail.bridge) parts.push('bridge');
    if (detail.tunnel) parts.push('tunnel');
    if (!detail.drivable) parts.push('path');
    this.roadDebugMeta.textContent = parts.join(' · ');
  }

  getMinimapZoomInButton(): HTMLButtonElement {
    return this.minimapZoomIn;
  }

  getMinimapZoomOutButton(): HTMLButtonElement {
    return this.minimapZoomOut;
  }

  getSettingsButton(): HTMLButtonElement {
    return this.settingsButton;
  }

  /** Update the zoom-range label and the disabled state of the +/- buttons. */
  setMinimapZoomState(viewRadiusM: number, canIn: boolean, canOut: boolean): void {
    this.minimapZoomLabel.textContent = formatRadius(viewRadiusM);
    this.minimapZoomIn.disabled = !canIn;
    this.minimapZoomOut.disabled = !canOut;
  }

  /** Update the "current area" line under the minimap canvas. Pass an ordered list of
   *  region names, most-specific first (e.g. ["Flogsta", "Uppsala kommun"]). Pass empty
   *  array to hide the row entirely. The first entry is bold; the rest are dim. */
  setMinimapArea(parts: readonly string[]): void {
    if (parts.length === 0) {
      this.minimapAreaLabel.hidden = true;
      this.minimapAreaLabel.replaceChildren();
      return;
    }
    this.minimapAreaLabel.hidden = false;
    const primary = document.createElement('span');
    primary.className = 'area-primary';
    primary.textContent = parts[0] ?? '';
    this.minimapAreaLabel.replaceChildren(primary);
    if (parts.length > 1) {
      const secondary = document.createElement('span');
      secondary.className = 'area-secondary';
      secondary.textContent = ` · ${parts.slice(1).join(' · ')}`;
      this.minimapAreaLabel.appendChild(secondary);
    }
  }

  /** Update the toggle button to offer the OTHER vehicle as its next action. Called by
   *  main.ts after a successful swap (and on initial load). */
  setActiveVehicle(kind: VehicleKind): void {
    this.vehicleToggleLabel.textContent =
      kind === 'car' ? 'Switch to motorcycle' : 'Switch to car';
  }

  // --------- Saved locations ---------

  private saveCurrent(): void {
    if (!this.activeLocation) return;
    save(this.activeLocation);
    this.refreshSavedList();
    this.setStatus(`Saved ${displayShort(this.activeLocation.displayName)}`);
  }

  private toggleSavedPanel(): void {
    if (this.savedPanel.hidden) {
      this.refreshSavedList();
      this.savedPanel.hidden = false;
      this.savedToggle.setAttribute('aria-expanded', 'true');
    } else {
      this.closeSavedPanel();
    }
  }

  private closeSavedPanel(): void {
    this.savedPanel.hidden = true;
    this.savedToggle.setAttribute('aria-expanded', 'false');
  }

  private refreshSavedList(): void {
    const list = loadAll();
    this.savedToggleLabel.textContent = list.length > 0 ? `Saved (${list.length})` : 'Saved';
    this.savedPanel.replaceChildren();
    if (list.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'saved-empty';
      empty.textContent = 'No saved locations yet.';
      this.savedPanel.appendChild(empty);
      return;
    }
    for (const loc of list) {
      const row = document.createElement('div');
      row.className = 'saved-item';

      const loadBtn = document.createElement('button');
      loadBtn.type = 'button';
      loadBtn.className = 'saved-item-load';
      loadBtn.textContent = loc.shortName;
      loadBtn.title = loc.displayName;
      loadBtn.addEventListener('click', () => {
        this.closeSavedPanel();
        this.handlers.onLoadSaved(loc);
      });

      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'saved-item-delete';
      delBtn.textContent = '×';
      delBtn.title = `Delete ${loc.shortName}`;
      delBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        remove(loc.displayName);
        this.refreshSavedList();
      });

      row.appendChild(loadBtn);
      row.appendChild(delBtn);
      this.savedPanel.appendChild(row);
    }
  }

  private updateSaveButton(): void {
    this.saveButton.disabled = this.activeLocation === null;
  }
}

function required(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`HUD element #${id} missing`);
  return el;
}

function requiredSelector(selector: string): HTMLElement {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) throw new Error(`HUD element ${selector} missing`);
  return el;
}

function displayShort(displayName: string): string {
  return displayName.split(',')[0]?.trim() ?? displayName;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatRadius(meters: number): string {
  if (meters >= 1000) {
    const km = meters / 1000;
    return Number.isInteger(km) ? `${km} km` : `${km.toFixed(1)} km`;
  }
  return `${meters} m`;
}
